import { ArrowRight, MapPin, Store, AlertCircle } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { OutletLocationMapping } from '@/lib/import/sources';

interface Location {
  id: string;
  name: string;
}

interface OutletMappingStepProps {
  detectedOutlets: string[];
  outletMapping: OutletLocationMapping;
  onMappingChange: (mapping: OutletLocationMapping) => void;
  locations: Location[];
}

/**
 * UI step where the user maps source POS outlets/locations
 * to BlazePOS locations before importing.
 */
export function OutletMappingStep({
  detectedOutlets,
  outletMapping,
  onMappingChange,
  locations,
}: OutletMappingStepProps) {
  const handleOutletMappingChange = (outletName: string, locationId: string) => {
    const newMapping = new Map(outletMapping);
    if (locationId === '__skip__') {
      newMapping.delete(outletName);
    } else {
      newMapping.set(outletName, locationId);
    }
    onMappingChange(newMapping);
  };

  const mappedCount = outletMapping.size;

  return (
    <div className="space-y-6">
      {/* Info banner */}
      <div className="flex items-start gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
        <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
        <div className="space-y-1">
          <p className="text-sm font-medium text-blue-700 dark:text-blue-400">
            Map your outlets to BlazePOS locations
          </p>
          <p className="text-xs text-muted-foreground">
            We detected {detectedOutlets.length} outlet{detectedOutlets.length !== 1 ? 's' : ''} in
            your export file. Map each outlet to a BlazePOS location to import inventory data.
            Outlets set to "Skip" will not have their inventory imported.
          </p>
        </div>
      </div>

      {/* Mapping table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="grid grid-cols-[1fr_auto_1fr] gap-0 items-center bg-muted/50 px-4 py-3 border-b">
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <Store className="h-4 w-4" />
            Source Outlet
          </div>
          <div className="w-8" />
          <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
            <MapPin className="h-4 w-4" />
            BlazePOS Location
          </div>
        </div>

        <div className="divide-y">
          {detectedOutlets.map((outletName) => {
            // Clean up outlet name for display (replace underscores, dashes)
            const displayName = outletName
              .replace(/_/g, ' ')
              .replace(/-/g, ' ');

            const currentValue = outletMapping.get(outletName) ?? '__skip__';

            return (
              <div
                key={outletName}
                className="grid grid-cols-[1fr_auto_1fr] gap-0 items-center px-4 py-3"
              >
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-muted-foreground/50" />
                  <span className="text-sm font-medium">{displayName}</span>
                </div>

                <div className="flex items-center justify-center w-8">
                  <ArrowRight className="h-4 w-4 text-muted-foreground" />
                </div>

                <Select
                  value={currentValue}
                  onValueChange={(val) => handleOutletMappingChange(outletName, val)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Select location..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__skip__">
                      <span className="text-muted-foreground">Skip (do not import inventory)</span>
                    </SelectItem>
                    {locations.map((loc) => (
                      <SelectItem key={loc.id} value={loc.id}>
                        {loc.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
        </div>
      </div>

      {/* Summary */}
      <div className="text-sm text-muted-foreground text-center">
        {mappedCount} of {detectedOutlets.length} outlet{detectedOutlets.length !== 1 ? 's' : ''} mapped
        {mappedCount === 0 && (
          <span className="block mt-1">
            You can proceed without mapping -- products will be imported without inventory data.
          </span>
        )}
      </div>
    </div>
  );
}
